/**
 * Patologías API Service
 * CRUD operations for patient pathology/clinical history management
 */

import { api } from "@/lib/api";

const PATOLOGIAS_URL = "/patologias/";

// =============================================================================
// TYPES
// =============================================================================

export interface Patologia {
  id: number;
  paciente: number;
  paciente_nombre: string;
  nombre: string;
  fecha_registro: string;
  activa: boolean;
  notas: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PatologiaListItem {
  id: number;
  paciente: number;
  paciente_nombre: string;
  nombre: string;
  fecha_registro: string;
  activa: boolean;
  notas: string;
  is_active: boolean;
}

export interface PatologiaCreateData {
  paciente: number;
  nombre: string;
  fecha_registro?: string;
  activa?: boolean;
  notas?: string;
}

export interface PatologiaUpdateData {
  nombre?: string;
  fecha_registro?: string;
  activa?: boolean;
  notas?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface PatologiaFilters {
  paciente?: number;
  activa?: boolean;
  is_active?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get paginated list of patologías
 */
export async function getPatologias(
  params: PatologiaFilters = {}
): Promise<PaginatedResponse<PatologiaListItem>> {
  const searchParams = new URLSearchParams();

  if (params.paciente) searchParams.append("paciente", String(params.paciente));
  if (params.activa !== undefined) searchParams.append("activa", String(params.activa));
  if (params.is_active !== undefined) searchParams.append("is_active", String(params.is_active));
  if (params.search) searchParams.append("search", params.search);
  if (params.page) searchParams.append("page", String(params.page));
  if (params.page_size) searchParams.append("page_size", String(params.page_size));
  if (params.ordering) searchParams.append("ordering", params.ordering);

  const url = searchParams.toString() ? `${PATOLOGIAS_URL}?${searchParams}` : PATOLOGIAS_URL;
  const response = await api.get<PaginatedResponse<PatologiaListItem>>(url);
  return response.data;
}

/**
 * Get a single patología by ID
 */
export async function getPatologia(id: number): Promise<Patologia> {
  const response = await api.get<Patologia>(`${PATOLOGIAS_URL}${id}/`);
  return response.data;
}

/**
 * Create a new patología
 */
export async function createPatologia(data: PatologiaCreateData): Promise<Patologia> {
  const response = await api.post<Patologia>(PATOLOGIAS_URL, data);
  return response.data;
}

/**
 * Update an existing patología
 */
export async function updatePatologia(id: number, data: PatologiaUpdateData): Promise<Patologia> {
  const response = await api.patch<Patologia>(`${PATOLOGIAS_URL}${id}/`, data);
  return response.data;
}

/**
 * Hard delete a patología (permanent removal)
 */
export async function deletePatologia(id: number): Promise<void> {
  await api.delete(`${PATOLOGIAS_URL}${id}/`);
}

/**
 * Deactivate a patología (soft delete)
 */
export async function deactivatePatologia(id: number): Promise<Patologia> {
  const response = await api.post<Patologia>(`${PATOLOGIAS_URL}${id}/deactivate/`);
  return response.data;
}

/**
 * Activate a patología
 */
export async function activatePatologia(id: number): Promise<Patologia> {
  const response = await api.post<Patologia>(`${PATOLOGIAS_URL}${id}/activate/`);
  return response.data;
}
